import CloseIcon from '@mui/icons-material/Close'
import { IconButton, Link } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LogoMobileES from '../../../assets/images/logos/logo-es.png'
import LogoMobilePT from '../../../assets/images/logos/logo-pt.png'
import MBrasil from '../../../assets/images/mapas/Brasil.png'
import MPeru from '../../../assets/images/mapas/Peru.png'
import MArgentina from '../../../assets/images/mapas/Argentina.png'
import MMexico from '../../../assets/images/mapas/Mexico.png'
import MChile from '../../../assets/images/mapas/Chile.png'
import MBolivia from '../../../assets/images/mapas/Bolivia.png'
import MGuyana from '../../../assets/images/mapas/Guyana.png'
import MParaguay from '../../../assets/images/mapas/Paraguay.png'

import FiltersContext from '../../../Context/FiltersContext'
import i18n from '../../../i18n/i18n'
import { COUNTRY_COLORS, CountryKeys } from '../../constants/country-color'
import './ProjectModal.scss'

export const ProjectModal = ({
  containerRef,
  posTop,
}: {
  containerRef: React.RefObject<HTMLDivElement>
  posTop: number
}) => {
  const { t }: any = useTranslation()
  const { projectSelected, setProjectSelected } = useContext(FiltersContext)
  const [country, setCountry] = useState<CountryKeys>('peru')
  const [projectLinks, setProjectLinks] = useState<string[]>([])

  const usFormatter = new Intl.NumberFormat('en-US')
  const esFormatter = new Intl.NumberFormat('es-CO')
  const ptFormatter = new Intl.NumberFormat('pt-BR')

  const isTallIframe = window.innerHeight > 2100

  // Function to determine which logo to display based on the language and device type
  const findLogo = () => {
    return i18n.language === 'es' || i18n.language.includes('en')
      ? LogoMobileES
      : LogoMobilePT
  }

  useEffect(() => {
    if (!!projectSelected) {
      setCountry(
        projectSelected.country
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
      )

      const links = [
        projectSelected['story_1'],
        projectSelected['story_2'],
        projectSelected['story_3'],
      ].filter((link) => !!link)

      setProjectLinks(links)
    }
  }, [projectSelected])

  const handleProjectModalClose = () => {
    setProjectSelected(null)
  }

  const getLanguageName = (lg: string) => {
    switch (lg) {
      case 'es':
        return 'spanish'
      case 'pt':
        return 'portuguese'
      case 'en':
        return 'english'
    }
  }

  const getMapImage = (country: CountryKeys) => {
    switch (country) {
      case 'brazil':
        return MBrasil
      case 'mexico':
        return MMexico
      case 'peru':
        return MPeru
      case 'argentina':
        return MArgentina
      case 'chile':
        return MChile
      case 'bolivia':
        return MBolivia
      case 'guyana':
        return MGuyana
      case 'paraguay':
        return MParaguay
    }
  }

  return (
    <>
      {projectSelected ? (
        <Dialog
          open={!!projectSelected}
          onClose={handleProjectModalClose}
          fullWidth={true}
          maxWidth="xl"
          className="project-modal"
          container={containerRef.current} // hace que el modal se renderice dentro del contenedor padre
          {...(isTallIframe && {
            // si el iframe es muy alto, hace que el modal se posicione de forma absoluta
            scroll: 'body',
            slotProps: {
              paper: {
                sx: {
                  position: 'absolute',
                  top: posTop,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  margin: 0,
                },
              },
            },
          })}
        >
          <DialogTitle
            id="responsive-dialog-title"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              paddingTop: 0,
              position: 'relative',
            }}
          >
            <div className="project-title_container">
              <span className="project-modal_logo">
                <img src={findLogo()} alt="Carbono Opaco" />
              </span>
              <div>
                <span className="project-title">
                  {projectSelected.property_name_or_project_name}
                </span>
                {projectSelected.project_url ? (
                  <Link
                    href={projectSelected.project_url}
                    color="#002665"
                    sx={{
                      typography: 'body1',
                    }}
                    className="project-link"
                    title={projectSelected.property_name_or_project_name}
                    target="_blank"
                  >
                    {t('project.visit_project_website')}
                    {/* {projectSelected.project_url} Go to Project */}
                    {/** preguntar si este es el valor de aca*/}
                  </Link>
                ) : null}
              </div>
            </div>
            <IconButton
              className="close-modal"
              onClick={handleProjectModalClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <div className="project-info">
              <div className="project-info_location">
                <div className="project-info_location-pais">
                  <div>
                    <span className="project-info_keyTag project-info_keyTag-bottom">
                      {t('project.country').toUpperCase()}
                    </span>
                    <p>
                      <span
                        style={{
                          padding: 2,
                          backgroundColor: COUNTRY_COLORS[country],
                          color: country === 'peru' ? '#fff' : '#000',
                        }}
                      >
                        {projectSelected.country.toUpperCase()}
                      </span>
                    </p>
                  </div>
                  <img
                    src={getMapImage(country)}
                    alt="mapa"
                    className="project-info_location-map"
                  />
                </div>
                <div className="project-info_location-departamento">
                  <span className="project-info_keyTag project-info_keyTag-bottom">
                    {t('project.departament').toUpperCase()}
                  </span>{' '}
                  <p>
                    {projectSelected.state_or_province_or_region_or_department
                      .length > 0
                      ? projectSelected.state_or_province_or_region_or_department.join(
                          ', '
                        )
                      : t('no_disponible')}
                  </p>
                </div>
                <div className="project-info_location-municipio">
                  <span className="project-info_keyTag project-info_keyTag-bottom">
                    {t('project.municipality').toUpperCase()}
                  </span>{' '}
                  <p>
                    {projectSelected.municipality.length > 0
                      ? projectSelected.municipality.join(', ')
                      : t('no_disponible')}
                  </p>
                </div>
                <div className="project-info_location-region">
                  <div>
                    <span className="project-info_keyTag project-info_keyTag-bottom">
                      {t('project.bioma_region').toUpperCase()}
                    </span>{' '}
                    <p>
                      {projectSelected.bioma_or_region
                        .map((id: string) => {
                          return t(`biomas.${id.trim()}`)
                        })
                        .join(', ')}
                    </p>
                  </div>
                </div>
              </div>
              {/* general aqui */}
              <div className="project-info_general">
                {/* left */}
                <div className="project-info_general-left">
                  <div>
                    <span className="project-info_keyTag project-info_keyTag-bottom">
                      {t('project.other_names').toUpperCase()}
                    </span>{' '}
                    <p className="project-info_dataKey">
                      {projectSelected.other_names || t('no_disponible')}
                    </p>
                  </div>
                  <div>
                    <span className="project-info_keyTag project-info_keyTag-bottom">
                      {t('project.phase_or_status').toUpperCase()}
                    </span>{' '}
                    <p className="project-info_dataKey">
                      {t(`project.states.${projectSelected.phase_or_status}`) ||
                        t('no_disponible')}
                    </p>
                  </div>
                  <div>
                    <span className="project-info_keyTag project-info_keyTag-bottom">
                      {t('project.deposit_type').toUpperCase()}
                    </span>{' '}
                    <p className="project-info_dataKey">
                      {t(`lithium_types.${projectSelected.lithium_type}`) ||
                        t('no_disponible')}
                    </p>
                  </div>
                </div>
                {/* center */}
                <div className="project-info_general-center">
                  <div>
                    <span
                      className="project-info_keyTag project-info_keyTag-right"
                      style={{ width: '95px', textAlign: 'right' }}
                    >
                      {t('project.overlap_with_protected_area').toUpperCase()}
                    </span>{' '}
                    <span className="project-info_dataKey">
                      {projectSelected.area_overlap
                        ? t(`project.overlap.${projectSelected.area_overlap}`)
                        : t('no_disponible')}
                    </span>
                  </div>
                  <div>
                    <span
                      className="project-info_keyTag project-info_keyTag-right"
                      style={{ width: '95px', textAlign: 'right' }}
                    >
                      {t('project.protected_area').toUpperCase()}
                    </span>{' '}
                    <span className="project-info_dataKey">
                      {projectSelected.area_name.length > 0
                        ? projectSelected.area_name
                            .map((name: string) =>
                              t(`project.area_name.${name}`)
                            )
                            .join(', ')
                        : t('no_disponible')}
                    </span>
                  </div>
                  <div>
                    <span
                      className="project-info_keyTag project-info_keyTag-right"
                      style={{ width: '95px', textAlign: 'right' }}
                    >
                      {t('project.community_overlap').toUpperCase()}
                    </span>{' '}
                    <span className="project-info_dataKey">
                      {projectSelected.community_overlap
                        ? t(
                            `project.overlap.${projectSelected.community_overlap}`
                          )
                        : t('no_disponible')}
                    </span>
                  </div>
                  <div>
                    <span
                      className="project-info_keyTag project-info_keyTag-right"
                      style={{ width: '95px', textAlign: 'right' }}
                    >
                      {t('project.community_name').toUpperCase()}
                    </span>{' '}
                    <span className="project-info_dataKey">
                      {projectSelected.community_name || t('no_disponible')}
                    </span>
                  </div>
                </div>
                {/* right */}
                <div className="project-info_general-right">
                  <div>
                    <div style={{ overflowY: 'auto' }}>
                      <span className="project-info_keyTag project-info_keyTag-bottom">
                        {t('project.owner_company').toUpperCase()}
                      </span>{' '}
                      <p
                        className="project-info_dataKey"
                        style={{ fontSize: '18px' }}
                      >
                        {projectSelected.empresas.join(',\n') ||
                          t('no_disponible')}
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          padding: 0,
                          // justifyContent: 'flex-end',
                          alignItems: 'flex-end',
                        }}
                      >
                        {projectSelected.technical_report_url ? (
                          <a
                            href={projectSelected.technical_report_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '14px',
                              color: '#95C11C',
                              // width: '100px',
                              // padding: '0 0 0 10px',
                              margin: 0,
                              fontWeight: 'bold',
                            }}
                          >
                            {t('project.technical_report')}
                          </a>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div>
                      <span className="project-info_keyTag project-info_keyTag-bottom">
                        {t('project.construction_year').toUpperCase()}
                      </span>
                      <p className="project-info_dataKey">
                        {projectSelected.year_construction_started &&
                        projectSelected.year_construction_started !== 'na'
                          ? projectSelected.year_construction_started
                          : t('no_disponible')}
                      </p>
                    </div>
                  </div>
                  <div>
                    <div>
                      <span className="project-info_keyTag project-info_keyTag-bottom">
                        {t('project.production_year').toUpperCase()}
                      </span>
                      <p className="project-info_dataKey">
                        {projectSelected.year_production_started &&
                        projectSelected.year_production_started !== 'na'
                          ? projectSelected.year_production_started
                          : t('no_disponible')}
                      </p>
                    </div>
                  </div>
                  <div>
                    <div>
                      <span className="project-info_keyTag project-info_keyTag-bottom">
                        {t('project.known_buyers').toUpperCase()}
                      </span>
                      <p className="project-info_dataKey">
                        {projectSelected.known_lithium_buyers ||
                          t('no_disponible')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {projectSelected[
              `${getLanguageName(i18n.language.substring(0, 2))}_profile`
            ] ? (
              <div className="project-info_description">
                <div className="project-info_description-box">
                  <div className="project-info_description-text">
                    <p>
                      {
                        projectSelected[
                          `${getLanguageName(i18n.language.substring(0, 2))}_profile`
                        ]
                      }
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {projectLinks && projectLinks.length > 0 && (
              <div className="project-info_links">
                <ul className="project-info_links-list">
                  <li>
                    <span style={{ fontWeight: 'bold', color: '#0D00FF' }}>
                      {t('project.knowTheStories')}:
                    </span>
                  </li>
                  {projectLinks?.map((link: any, index: number) => (
                    <li
                      key={index}
                      style={{
                        width: (100 / projectLinks.length + 1) / 100 + '%',
                      }}
                    >
                      <Link
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="#008e38"
                        // sx={{
                        //   typography: 'body1',
                        //   fontSize: '12px',
                        // }}
                      >
                        {t('project.story')} {index + 1}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  )
}
